import { useState, useEffect } from "react";
import Form from "react-bootstrap/Form";
import Table from "react-bootstrap/Table";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ReactSVG } from "react-svg";
import editicon from "../../../assets/images/edit-icon.svg";
import deleteicon from "../../../assets/images/delete-icon.svg";
import Select from "react-select";
import * as Yup from "yup";
import { useFormik } from "formik";
import "./addvendor.scss";
import useLocations from "../../../Hooks/useLocations";
import useMckesson from "../../../Hooks/useMckesson";
import Loading from "../../../components/LoadingPage/Loading";

interface ShippingLocation {
  Id: string;
  Name: string;
  Address: string;
  City: string;
  State: string;
  Zip: string;
  Location: string;
  Default: boolean;
}

interface UrlEntry {
  Identifier: string;
  URL: string;
}

interface VendorSettingsData {
  ClientName: string;
  Identity: string;
  SharedSecret: string;
  Account: string;
  ExternalOrderRefNoPrefix: string;
  ShippingLocations: ShippingLocation[];
  APIs: UrlEntry[];
}

export interface McKessonVendorDetail {
  id: string;
  vendorMasterId: string;
  clientName: string;
  identity: string;
  sharedSecret: string;
  account: string;
  externalOrderRefNoPrefix: string;
}

export interface McKessonShippingLocation {
  id: string;
  vendorMasterId: string;
  shippingLocationId: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  isDefault: boolean;
  mappedLocationId: string | null;
}

export interface McKessonApiEntry {
  id: string;
  vendorMasterId: string;
  identifier: string;
  url: string;
}

interface VendorSettingsTabProps {
  vendorDetail: McKessonVendorDetail | null;
  shippingLocations: McKessonShippingLocation[];
  apiEntries: McKessonApiEntry[];
  onSave: (settings: string) => void;
  onRefreshApis: () => void;
  onRefreshShippingLocations: () => void;
  onRefreshVendorDetail: () => void;
  isLoading?: boolean;
}

const defaultSettings: VendorSettingsData = {
  ClientName: "",
  Identity: "",
  SharedSecret: "",
  Account: "",
  ExternalOrderRefNoPrefix: "",
  ShippingLocations: [],
  APIs: [],
};

const emptyShippingLocation: ShippingLocation = {
  Id: "",
  Name: "",
  Address: "",
  City: "",
  State: "",
  Zip: "",
  Location: "",
  Default: false,
};

const emptyUrl: UrlEntry = {
  Identifier: "",
  URL: "",
};

const VendorSettingsTab: React.FC<VendorSettingsTabProps> = ({
  vendorDetail,
  shippingLocations: apiShippingLocations,
  apiEntries,
  onSave,
  onRefreshApis,
  onRefreshShippingLocations,
  onRefreshVendorDetail,
  isLoading,
}) => {
  const [settings, setSettings] = useState<VendorSettingsData>(defaultSettings);
  const [editingShippingIndex, setEditingShippingIndex] = useState<number | null>(null);
  const [editingShippingLocationId, setEditingShippingLocationId] = useState<string | null>(null);
  const [editingUrlIndex, setEditingUrlIndex] = useState<number | null>(null);
  const [editingApiEntryId, setEditingApiEntryId] = useState<string | null>(null);
  const [locationOptions, setLocationOptions] = useState<{ value: string; label: string }[]>([]);

  const { getLocationsAPI } = useLocations();
  const {
    postMckessonApiAPI, postMckessonApiLoading,
    putMckessonApiAPI, putMckessonApiLoading,
    deleteMckessonApiAPI, deleteMckessonApiLoading,
    postMckessonShippingLocationAPI, postMckessonShippingLocationLoading,
    putMckessonShippingLocationAPI, putMckessonShippingLocationLoading,
    deleteMckessonShippingLocationAPI, deleteMckessonShippingLocationLoading,
    postMckessonVendorDetailAPI, postMckessonVendorDetailLoading,
    putMckessonVendorDetailAPI, putMckessonVendorDetailLoading,
  } = useMckesson();

  // Shipping Location Formik
  const shippingFormik = useFormik({
    initialValues: emptyShippingLocation,
    validationSchema: Yup.object({
      Id: Yup.string().required("Id is required"),
      Name: Yup.string().required("Name is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          vendorMasterId: vendorDetail?.vendorMasterId || "",
          shippingLocationId: values.Id,
          name: values.Name,
          address: values.Address,
          city: values.City,
          state: values.State,
          zip: values.Zip,
          isDefault: values.Default,
          mappedLocationId: values.Location || null,
        };
        if (editingShippingIndex !== null && editingShippingLocationId) {
          await putMckessonShippingLocationAPI({
            id: editingShippingLocationId,
            ...payload,
          });
          setEditingShippingIndex(null);
          setEditingShippingLocationId(null);
        } else {
          await postMckessonShippingLocationAPI(payload);
        }
        resetForm();
        onRefreshShippingLocations();
      } catch {
        // error handled by hook
      }
    },
    enableReinitialize: true,
  });

  // URL Formik
  const urlFormik = useFormik({
    initialValues: emptyUrl,
    validationSchema: Yup.object({
      Identifier: Yup.string().required("Identifier is required"),
      URL: Yup.string().required("URL is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        if (editingUrlIndex !== null && editingApiEntryId) {
          await putMckessonApiAPI({
            id: editingApiEntryId,
            vendorMasterId: vendorDetail?.vendorMasterId || "",
            identifier: values.Identifier,
            url: values.URL,
          });
          setEditingUrlIndex(null);
          setEditingApiEntryId(null);
        } else {
          await postMckessonApiAPI({
            vendorMasterId: vendorDetail?.vendorMasterId || "",
            identifier: values.Identifier,
            url: values.URL,
          });
        }
        resetForm();
        onRefreshApis();
      } catch {
        // error handled by hook
      }
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (vendorDetail) {
      setSettings((prev) => ({
        ...prev,
        ClientName: vendorDetail.clientName || "",
        Identity: vendorDetail.identity || "",
        SharedSecret: vendorDetail.sharedSecret || "",
        Account: vendorDetail.account || "",
        ExternalOrderRefNoPrefix: vendorDetail.externalOrderRefNoPrefix || "",
      }));
    } else {
      setSettings(defaultSettings);
    }
    loadLocations();
  }, [vendorDetail]);

  useEffect(() => {
    const mapped: ShippingLocation[] = apiShippingLocations.map((loc) => ({
      Id: loc.shippingLocationId || "",
      Name: loc.name || "",
      Address: loc.address || "",
      City: loc.city || "",
      State: loc.state || "",
      Zip: loc.zip || "",
      Location: loc.mappedLocationId || "",
      Default: loc.isDefault || false,
    }));
    setSettings((prev) => ({ ...prev, ShippingLocations: mapped }));
    shippingFormik.resetForm();
  }, [apiShippingLocations]);

  useEffect(() => {
    const mapped: UrlEntry[] = apiEntries.map((api) => ({
      Identifier: api.identifier || "",
      URL: api.url || "",
    }));
    setSettings((prev) => ({ ...prev, APIs: mapped }));
    urlFormik.resetForm();
  }, [apiEntries]);

  const loadLocations = async () => {
    try {
      const res = await getLocationsAPI({
        searchText: "",
        includeDeleted: false,
        active: true,
        hasMappedLocation: false,
      });
      setLocationOptions(
        res?.map((option: any) => ({
          value: option.id,
          label: option.locationName,
        })) || [],
      );
    } catch {
      // ignore
    }
  };

  const handleFieldChange = (field: keyof VendorSettingsData, value: string) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditShippingLocation = (index: number) => {
    shippingFormik.setValues(settings.ShippingLocations[index]);
    setEditingShippingIndex(index);
    setEditingShippingLocationId(apiShippingLocations[index]?.id || null);
  };

  const handleCancelShipping = () => {
    shippingFormik.resetForm();
    setEditingShippingIndex(null);
    setEditingShippingLocationId(null);
  };

  const handleDeleteShippingLocation = async (index: number) => {
    const entry = apiShippingLocations[index];
    if (!entry?.id) return;
    try {
      await deleteMckessonShippingLocationAPI({ id: entry.id });
      onRefreshShippingLocations();
    } catch {
      // error handled by hook
    }
  };

  const handleEditUrl = (index: number) => {
    urlFormik.setValues(settings.APIs[index]);
    setEditingUrlIndex(index);
    setEditingApiEntryId(apiEntries[index]?.id || null);
  };

  const handleCancelUrl = () => {
    urlFormik.resetForm();
    setEditingUrlIndex(null);
    setEditingApiEntryId(null);
  };

  const handleDeleteUrl = async (index: number) => {
    const entry = apiEntries[index];
    if (!entry?.id) return;
    try {
      await deleteMckessonApiAPI({ id: entry.id });
      onRefreshApis();
    } catch {
      // error handled by hook
    }
  };

  const handleSaveSettings = async () => {
    const payload = {
      vendorMasterId: vendorDetail?.vendorMasterId || "",
      clientName: settings.ClientName,
      identity: settings.Identity,
      sharedSecret: settings.SharedSecret,
      account: settings.Account,
      externalOrderRefNoPrefix: settings.ExternalOrderRefNoPrefix,
    };
    try {
      if (vendorDetail?.id) {
        await putMckessonVendorDetailAPI({ id: vendorDetail.id, ...payload });
      } else {
        await postMckessonVendorDetailAPI(payload);
      }
      onRefreshVendorDetail();
    } catch {
      // error handled by hook
    }
  };

  return (
    <div className="vendor-settings-tab">
      {(isLoading || postMckessonVendorDetailLoading || putMckessonVendorDetailLoading || postMckessonApiLoading || putMckessonApiLoading || deleteMckessonApiLoading || postMckessonShippingLocationLoading || putMckessonShippingLocationLoading || deleteMckessonShippingLocationLoading) && <Loading />}
      <Form>
        {/* Basic Fields */}
        <div className="settings-fields">
          <Form.Group className="mb-3">
            <Form.Label>Identity</Form.Label>
            <Form.Control
              type="text"
              value={settings.Identity}
              onChange={(e) => handleFieldChange("Identity", e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Shared Secret</Form.Label>
            <Form.Control
              type="text"
              value={settings.SharedSecret}
              onChange={(e) => handleFieldChange("SharedSecret", e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Account</Form.Label>
            <Form.Control
              type="text"
              value={settings.Account}
              onChange={(e) => handleFieldChange("Account", e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Ref No Prefix</Form.Label>
            <Form.Control
              type="text"
              value={settings.ExternalOrderRefNoPrefix}
              onChange={(e) => handleFieldChange("ExternalOrderRefNoPrefix", e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Client Name</Form.Label>
            <Form.Control
              type="text"
              value={settings.ClientName}
              onChange={(e) => handleFieldChange("ClientName", e.target.value)}
            />
          </Form.Group>
        </div>

        {/* Shipping Locations Section */}
        <div className="settings-section">
          <h6 className="section-title">Shipping Locations</h6>
          <div className="section-form">
            <Form.Group>
              <Form.Label>Id<sup>*</sup></Form.Label>
              <Form.Control
                type="text"
                name="Id"
                value={shippingFormik.values.Id}
                onChange={shippingFormik.handleChange}
                isInvalid={shippingFormik.touched.Id && !!shippingFormik.errors.Id}
              />
              {shippingFormik.touched.Id && shippingFormik.errors.Id && (
                <Form.Control.Feedback type="invalid">
                  {shippingFormik.errors.Id}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group>
              <Form.Label>Name<sup>*</sup></Form.Label>
              <Form.Control
                type="text"
                name="Name"
                value={shippingFormik.values.Name}
                onChange={shippingFormik.handleChange}
                isInvalid={shippingFormik.touched.Name && !!shippingFormik.errors.Name}
              />
              {shippingFormik.touched.Name && shippingFormik.errors.Name && (
                <Form.Control.Feedback type="invalid">
                  {shippingFormik.errors.Name}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group>
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="Address"
                value={shippingFormik.values.Address}
                onChange={shippingFormik.handleChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Location</Form.Label>
              <Select
                classNamePrefix="react-select"
                options={locationOptions}
                value={locationOptions.find((opt) => opt.value === shippingFormik.values.Location) || null}
                onChange={(option: any) =>
                  shippingFormik.setFieldValue("Location", option?.value || "")
                }
                placeholder="Select"
                isClearable
                className="react-select-container"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>City</Form.Label>
              <Form.Control
                type="text"
                name="City"
                value={shippingFormik.values.City}
                onChange={shippingFormik.handleChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>State</Form.Label>
              <Form.Control
                type="text"
                name="State"
                value={shippingFormik.values.State}
                onChange={shippingFormik.handleChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Zip</Form.Label>
              <Form.Control
                type="text"
                name="Zip"
                value={shippingFormik.values.Zip}
                onChange={shippingFormik.handleChange}
              />
            </Form.Group>
            <Form.Group className="default-checkbox">
              <Form.Check
                inline
                label="Default?"
                name="Default"
                type="checkbox"
                checked={shippingFormik.values.Default}
                onChange={shippingFormik.handleChange}
              />
            </Form.Group>
            <Form.Group className="section-form-actions">
              <Button variant="primary" size="sm" type="button" onClick={() => shippingFormik.handleSubmit()}>
                {editingShippingIndex !== null ? "Update Shipping Location" : "Add Shipping Location"}
              </Button>
              <Button variant="outline-primary" size="sm" type="button" onClick={handleCancelShipping}>
                Cancel
              </Button>
            </Form.Group>
          </div>

          {settings.ShippingLocations.length > 0 && (
            <div className="vendor-setting-table-wrapper">
              <Table responsive="md" hover className="settings-table">
                <thead>
                  <tr>
                    <th>Id</th>
                    <th>Name</th>
                    <th>Address</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Zip</th>
                    <th>Location</th>
                    <th>Default?</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {settings.ShippingLocations.map((loc, index) => (
                    <tr key={index}>
                      <td>{loc.Id}</td>
                      <td>{loc.Name}</td>
                      <td>{loc.Address}</td>
                      <td>{loc.City}</td>
                      <td>{loc.State}</td>
                      <td>{loc.Zip}</td>
                      <td>{loc.Location}</td>
                      <td>{loc.Default ? "True" : "False"}</td>
                      <td>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                          <span className="k-cursor-pointer me-2" onClick={() => handleEditShippingLocation(index)}>
                            <ReactSVG className="svg-box" wrapper="span" src={editicon} />
                          </span>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                          <span className="k-cursor-pointer" onClick={() => handleDeleteShippingLocation(index)}>
                            <ReactSVG className="svg-box" wrapper="span" src={deleteicon} />
                          </span>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>

        {/* URLs Section */}
        <div className="settings-section">
          <h6 className="section-title">Urls</h6>
          <div className="section-form url-row">
            <Form.Group>
              <Form.Label>Identifier<sup>*</sup></Form.Label>
              <Form.Control
                type="text"
                name="Identifier"
                value={urlFormik.values.Identifier}
                onChange={urlFormik.handleChange}
                isInvalid={urlFormik.touched.Identifier && !!urlFormik.errors.Identifier}
              />
              {urlFormik.touched.Identifier && urlFormik.errors.Identifier && (
                <Form.Control.Feedback type="invalid">
                  {urlFormik.errors.Identifier}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group className="url-field">
              <Form.Label>URL<sup>*</sup></Form.Label>
              <Form.Control
                type="text"
                name="URL"
                value={urlFormik.values.URL}
                onChange={urlFormik.handleChange}
                isInvalid={urlFormik.touched.URL && !!urlFormik.errors.URL}
              />
              {urlFormik.touched.URL && urlFormik.errors.URL && (
                <Form.Control.Feedback type="invalid">
                  {urlFormik.errors.URL}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group className="section-form-actions">
              <Button variant="primary" size="sm" type="button" onClick={() => urlFormik.handleSubmit()}>
                {editingUrlIndex !== null ? "Update URL" : "Add URL"}
              </Button>
              <Button variant="outline-primary" size="sm" type="button" onClick={handleCancelUrl}>
                Cancel
              </Button>
            </Form.Group>
          </div>

          {settings.APIs.length > 0 && (
            <div className="vendor-setting-table-wrapper">
              <Table responsive="md" hover className="settings-table">
                <thead>
                  <tr>
                    <th>Identifier</th>
                    <th>URL</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {settings.APIs.map((api, index) => (
                    <tr key={index}>
                      <td>{api.Identifier}</td>
                      <td>{api.URL}</td>
                      <td>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                          <span className="k-cursor-pointer me-2" onClick={() => handleEditUrl(index)}>
                            <ReactSVG className="svg-box" wrapper="span" src={editicon} />
                          </span>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                          <span className="k-cursor-pointer" onClick={() => handleDeleteUrl(index)}>
                            <ReactSVG className="svg-box" wrapper="span" src={deleteicon} />
                          </span>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="settings-footer">
          <Button variant="primary" type="button" onClick={handleSaveSettings}>
            Save Settings
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default VendorSettingsTab;
